import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { 
  Check, Phone, Mail, MapPin, Send, 
  MessageCircle, Users, Award, TrendingUp, Sparkles, Clock 
} from 'lucide-react';
import { companyInfo } from '../mock';
import SEO from '../components/SEO';
import countryCodes from '../utils/countryCodes';
import { useFormSubmit } from '../hooks/useFormSubmit';
import AOS from 'aos';
import 'aos/dist/aos.css';

const Contact = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+1',
    message: ''
  });

  // Use centralized form submission hook
  const { isSubmitting, formStatus, handleSubmit, geoData, isLoadingGeo } = useFormSubmit();

  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: 'ease-out',
      once: true,
      offset: 100
    });
  }, []);

  // Auto-select country based on geolocation
  useEffect(() => {
    if (geoData?.isSuccess && geoData?.data?.countryCode) {
      const matchingCountry = countryCodes.find(
        country => country.country === geoData.data.countryName
      );
      
      if (matchingCountry) {
        setContactForm(prev => ({
          ...prev,
          countryCode: matchingCountry.code
        }));
      }
    }
  }, [geoData]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit(contactForm);
    
    // Reset form only on success
    if (formStatus.type === 'success') {
      setContactForm({ 
        name: '', 
        email: '', 
        phone: '', 
        countryCode: '+1',
        message: ''
      });
    }
  };

  const contactMethods = [
    {
      icon: Phone,
      title: 'Call Us',
      value: companyInfo.phone,
      link: `tel:${companyInfo.phone}`,
      description: '',
      color: 'from-blue-500 to-blue-700'
    },
    {
      icon: Mail,
      title: 'Email Us',
      value: companyInfo.email,
      link: `mailto:${companyInfo.email}`,
      description: '',
      color: 'from-purple-500 to-purple-700'
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      value: companyInfo.address,
      link: '#',
      description: '',
      color: 'from-green-500 to-green-700'
    }
  ];

  const stats = [
    { number: '500+', label: 'Books Published', icon: Award },
    { number: '300+', label: 'Happy Authors', icon: Users },
    { number: '24h', label: 'Response Time', icon: Clock },
    { number: '98%', label: 'Satisfaction Rate', icon: TrendingUp }
  ];

  return (
    <div className="min-h-screen bg-[#000000] relative overflow-hidden">
      <SEO 
        title="Contact Us - Get Publishing Quote"
        description="Contact us for a free consultation. Speak with our publishing experts about your book project and get started today."
        keywords="contact book publisher, publishing consultation, book publishing quote, manuscript submission, get published, talk to publisher, book project inquiry"
        url="/contact"
      />

      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#F4D03F]/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#F4D03F]/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#F4D03F]/3 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Hero Section with Parallax Effect */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto" data-aos="fade-up">
            <div className="inline-flex items-center gap-2 bg-[#F4D03F]/10 border border-[#F4D03F]/30 rounded-full px-6 py-2 mb-6">
              <Sparkles className="text-[#F4D03F]" size={20} />
              <span className="text-[#F4D03F] font-semibold text-sm">Let's Start Your Publishing Journey</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#F4D03F] via-[#f8d47a] to-[#F4D03F] mb-6 uppercase tracking-tight">
              Get In Touch
            </h1>
            
            <p className="text-xl sm:text-2xl text-[#dfddd6] leading-relaxed mb-8">
              Have a story to tell? We're here to help you share it with the world.
            </p>

            {/* Stats Bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto" data-aos="fade-up" data-aos-delay="200">
              {stats.map((stat, index) => (
                <div 
                  key={index}
                  className="bg-[#1a1a1a]/80 backdrop-blur-sm border border-[#F4D03F]/20 rounded-xl p-4 hover:border-[#F4D03F]/50 transition-all duration-300 hover:scale-105"
                >
                  <stat.icon className="text-[#F4D03F] mx-auto mb-2" size={24} />
                  <div className="text-[#F4D03F] font-black text-2xl mb-1">{stat.number}</div>
                  <div className="text-[#888680] text-xs uppercase tracking-wide">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Methods Cards */}
      <section className="py-12 relative z-10">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-aos="fade-up">
            {contactMethods.map((method, index) => (
              <a
                key={index}
                href={method.link}
                className="group relative bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border-2 border-[#F4D03F]/20 rounded-2xl p-6 hover:border-[#F4D03F] transition-all duration-300 overflow-hidden"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                {/* Gradient Overlay on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${method.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 rounded-full bg-[#F4D03F]/10 flex items-center justify-center mb-4 group-hover:bg-[#F4D03F]/20 transition-colors">
                    <method.icon className="text-[#F4D03F]" size={28} />
                  </div>
                  
                  <h3 className="text-[#F4D03F] font-bold text-xl mb-2">{method.title}</h3>
                  <p className="text-[#dfddd6] text-sm mb-2 break-words">{method.value}</p>
                  <p className="text-[#888680] text-xs">{method.description}</p>
                </div>

                {/* Hover Arrow */}
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Send className="text-[#F4D03F]" size={20} />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-16 relative z-10">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
            
            {/* Contact Form - Enhanced Design */}
            <div data-aos="fade-right">
              <Card className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border-2 border-[#F4D03F]/30 overflow-hidden relative">
                {/* Decorative Corner */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#F4D03F]/10 rounded-bl-full"></div>
                
                <CardContent className="p-8 relative z-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-[#F4D03F]/20 flex items-center justify-center">
                      <MessageCircle className="text-[#F4D03F]" size={24} />
                    </div>
                    <div>
                      <h2 className="text-[#F4D03F] font-bold text-2xl">Send Us a Message</h2>
                      <p className="text-[#888680] text-sm">We'll respond within 24 hours</p>
                    </div>
                  </div>

                  <form onSubmit={handleContactSubmit} className="space-y-3">
                    <div>
                      <Input
                        type="text"
                        placeholder="John Doe"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                        aria-label="Your Name"
                        className="bg-[#000000] border-[#F4D03F]/30 text-[#dfddd6] placeholder:text-[#888680] h-12 focus:border-[#F4D03F] transition-all"
                      />
                    </div>

                    <div>
                      <Input
                        type="email"
                        placeholder="john@example.com"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        required
                        aria-label="Your Email"
                        className="bg-[#000000] border-[#F4D03F]/30 text-[#dfddd6] placeholder:text-[#888680] h-12 focus:border-[#F4D03F] transition-all"
                      />
                    </div>

                    {/* Country Display from Geolocation */}
                    {geoData?.isSuccess && geoData?.data?.countryName && (
                      <div className="bg-[#2C3E50]/30 border-2 border-[#F4D03F]/30 rounded-lg p-3 flex items-center gap-2 hidden">
                        <MapPin className="text-[#F4D03F]" size={18} />
                        <span className="text-[#dfddd6] text-sm">
                          Detected Location: <span className="text-[#F4D03F] font-semibold">{geoData.data.countryName}</span>
                        </span>
                      </div>
                    )}

                    <div>
                      <div className="flex gap-2">
                        <select
                          value={contactForm.countryCode}
                          onChange={(e) => setContactForm({ ...contactForm, countryCode: e.target.value })}
                          aria-label="Country Code"
                          className="bg-[#000000] border border-[#F4D03F]/30 text-[#dfddd6] rounded-md px-3 h-12 focus:outline-none focus:border-[#F4D03F] transition-all"
                          style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                        >
                          {countryCodes.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.country} {country.code}
                            </option>
                          ))}
                        </select>
                        <Input
                          type="tel"
                          placeholder="(123) 456-7890"
                          value={contactForm.phone}
                          onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                          aria-label="Your Phone"
                          className="flex-1 bg-[#000000] border-[#F4D03F]/30 text-[#dfddd6] placeholder:text-[#888680] h-12 focus:border-[#F4D03F] transition-all"
                        />
                      </div>
                    </div>

                    <div>
                      <Textarea
                        placeholder="Tell us about your book project..."
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        required
                        rows={6}
                        aria-label="Your Message"
                        className="bg-[#000000] border-[#F4D03F]/30 text-[#dfddd6] placeholder:text-[#888680] focus:border-[#F4D03F] transition-all resize-none"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="btn-primary w-full h-14 text-lg font-bold relative overflow-hidden group"
                    >
                      <span className={`flex items-center justify-center gap-2 ${isSubmitting ? 'opacity-0' : 'opacity-100'}`}>
                        <Send size={20} />
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </span>
                      {isSubmitting && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-6 h-6 border-3 border-[#000000] border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                    </Button>

                    {/* Form Status Message */}
                    {formStatus.message && (
                      <div className={`p-4 rounded-lg text-center font-medium ${
                        formStatus.type === 'success' 
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`}>
                        {formStatus.message}
                      </div>
                    )}

                    <p className="text-center text-[#888680] text-sm">
                      ✓ Free consultation • ✓ No obligation • ✓ Expert guidance
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Right Side - Info & Benefits */}
            <div className="space-y-6" data-aos="fade-left">
              
              {/* Why Choose Us - Creative Card */}
              <Card className="bg-gradient-to-br from-[#1a1a1a] via-[#1a1a1a] to-[#0a0a0a] border-2 border-[#F4D03F]/30 overflow-hidden relative">
                {/* Animated Background Pattern */}
                <div className="absolute inset-0 opacity-5">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `radial-gradient(circle at 2px 2px, #F4D03F 1px, transparent 0)`,
                    backgroundSize: '40px 40px'
                  }}></div>
                </div>

                <CardContent className="p-8 relative z-10">
                  <h3 className="text-[#F4D03F] font-black text-2xl mb-6 uppercase flex items-center gap-3">
                    <Award size={28} />
                    Why Choose Us?
                  </h3>
                  
                  <div className="grid gap-4">
                    {[
                      'Proven industry expertise',
                      '500+ successfully published books',
                      'Personalized author support',
                      'Global distribution network',
                      'Professional editing & design team',
                      'Fast turnaround times',
                      '100% author rights retention'
                    ].map((benefit, index) => (
                      <div 
                        key={index}
                        className="flex items-start gap-3 bg-[#000000]/30 p-4 rounded-lg hover:bg-[#000000]/50 transition-all group"
                      >
                        <div className="w-6 h-6 rounded-full bg-[#F4D03F]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#F4D03F]/30 transition-colors">
                          <Check className="text-[#F4D03F]" size={14} strokeWidth={3} />
                        </div>
                        <span className="text-[#dfddd6] text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className="py-16 relative z-10">
        <div className="container mx-auto px-4 sm:px-6">
          <div 
            className="max-w-4xl mx-auto bg-gradient-to-r from-[#F4D03F] to-[#f8d47a] rounded-3xl p-12 text-center relative overflow-hidden"
            data-aos="zoom-in"
          >
            {/* Decorative Elements */}
            <div className="absolute top-0 left-0 w-40 h-40 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-40 h-40 bg-white/10 rounded-full translate-x-1/2 translate-y-1/2"></div>
            
            <div className="relative z-10">
              <h2 className="text-[#000000] font-black text-4xl mb-4">
                Ready to Publish Your Book?
              </h2>
              <p className="text-[#000000]/80 text-lg mb-6">
                Get a free consultation today and let's bring your story to life!
              </p>
              <Button 
                className="bg-[#000000] text-[#F4D03F] hover:bg-[#1a1a1a] px-8 py-6 text-lg font-bold h-auto"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                Start Your Journey →
              </Button>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Contact;
